import React, { useState } from 'react';
import MailBody from './MailBody';
import AttachmentChip from './AttachmentChip';
import {
  parseAddress,
  formatFullDate,
  getInitials,
  avatarColor,
  getPreviewText,
} from '../utils/emailParsing';

/**
 * MailThreadMessage — a single message card inside a conversation.
 *
 * Each card carries its own sender header (avatar, name, direction, date) and
 * body. Cards default to open (per product decision: full history expanded),
 * but the header stays clickable to collapse/expand individual messages.
 */
export default function MailThreadMessage({ message, defaultOpen = true, isLatest = false }) {
  const [open, setOpen] = useState(defaultOpen);

  const personField = message.isOutgoing
    ? (message.to || message.recipients || '')
    : message.from;
  const person = parseAddress(personField);
  const date = formatFullDate(message.timestamp || message.date);

  const attachments = Array.isArray(message.attachments) ? message.attachments : [];

  return (
    <div className={`ivx-thread-msg ${open ? 'open' : 'collapsed'} ${isLatest ? 'is-latest' : ''}`}>
      <button
        type="button"
        className="ivx-thread-msg-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div
          className="ivx-thread-msg-avatar"
          style={{ backgroundColor: avatarColor(person.email || person.name) }}
        >
          {getInitials(person.name || person.email)}
        </div>

        <div className="ivx-thread-msg-meta">
          <div className="ivx-thread-msg-line1">
            <span className="ivx-thread-msg-name">
              {person.name || person.email || 'Unknown'}
            </span>
            <span className={`ivx-thread-dir ${message.isOutgoing ? 'out' : 'in'}`}>
              {message.isOutgoing ? 'Gesendet' : 'Empfangen'}
            </span>
          </div>
          {open ? (
            <div className="ivx-thread-msg-sub">
              {message.isOutgoing ? 'An: ' : 'Von: '}
              {person.email || person.name}
            </div>
          ) : (
            <div className="ivx-thread-msg-preview">{getPreviewText(message)}</div>
          )}
        </div>

        <span className="ivx-thread-msg-date">{date}</span>
        <svg
          className="ivx-thread-chevron"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className="ivx-thread-msg-body">
          <MailBody email={message} />

          {attachments.length > 0 && (
            <div className="ivx-detail-attachments">
              <span className="ivx-detail-attachments-label">
                {attachments.length} attachment{attachments.length > 1 ? 's' : ''}
              </span>
              <div className="ivx-detail-attachments-list">
                {attachments.map((a, i) => (
                  <AttachmentChip key={i} attachment={a} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
